import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import ErrorTriangle from "../../../components/Error/ErrorTriangle";
import type { AssetRegisterDataItem } from "../../../components/Table/AssetRegisterData.tsx";
import { useUpdateActiveWirelessReceiverIdList } from "../../../hooks/updateEntity";
import { useRequiredSelectedCompanyId } from "../../../hooks/useCompany";
import useUserHasRoles from "../../../hooks/user/getCurrentUserRole";
import {
  type AssetPosition,
  getAssetPositionWirelessReceiversAssetPositionAssetPositionIdWirelessReceiversGetQueryKey,
  useGetAssetPositionWirelessReceiversAssetPositionAssetPositionIdWirelessReceiversGet,
} from "../../../kubb";

export default function ReceiverAssignmentButton(props: {
  assetPosition: AssetPosition | AssetRegisterDataItem;
  receiverPositionId: string;
  scheme_id: string;
}): React.ReactElement {
  const company_id = useRequiredSelectedCompanyId();
  const { receiverPositionId, assetPosition } = props;
  const queryClient = useQueryClient();
  const isUserAdmin = useUserHasRoles("admin");

  // TanStack Query dedupes by query key, so every receiver button in a row
  // shares one request for that asset position's receiver list — no hand-rolled
  // per-id cache needed.
  const request =
    useGetAssetPositionWirelessReceiversAssetPositionAssetPositionIdWirelessReceiversGet(
      assetPosition.asset_position_id,
      { company_id },
      { query: { enabled: !!company_id } },
    );
  const receivers = request.data?.data;

  const {
    update,
    error,
    loading: updating,
  } = useUpdateActiveWirelessReceiverIdList(assetPosition, company_id);

  const assigned = !!receivers?.includes(receiverPositionId);

  function toggleAssignment() {
    if (!receivers || !isUserAdmin || updating) {
      return;
    }
    const updatedReceivers = assigned
      ? receivers.filter((r) => r !== receiverPositionId)
      : [...receivers, receiverPositionId];
    // Optimistically write the new list into the query cache once the update
    // succeeds (matches the old reducer's UPDATED_ITEM behaviour — no refetch).
    update(updatedReceivers).then(() => {
      queryClient.setQueryData<NonNullable<typeof request.data>>(
        getAssetPositionWirelessReceiversAssetPositionAssetPositionIdWirelessReceiversGetQueryKey(
          assetPosition.asset_position_id,
          { company_id },
        ),
        (old) => (old ? { ...old, data: updatedReceivers } : old),
      );
    });
  }

  const loading = request.isLoading;

  return (
    <div style={{ width: "20px" }} className={"align-middle"}>
      {(loading || updating) && (
        <FontAwesomeIcon icon={faSpinner} spin fixedWidth />
      )}
      {!loading && !updating && receivers && (
        <FontAwesomeIcon
          icon={assigned ? faCheckSquare : faSquare}
          onClick={toggleAssignment}
          role={isUserAdmin ? "button" : undefined}
          fixedWidth
        />
      )}
      {!loading && <ErrorTriangle error={request.error} />}
      {!updating && <ErrorTriangle error={error} />}
    </div>
  );
}
